// frontend/src/AdminMetrics.js
// ============================================================
// Your Scholarship Finder — Admin Metrics Dashboard (#84)
// ============================================================
// Import into Admin.js and render as a tab alongside existing
// Scholarships and Students tabs.
// ============================================================

import { useState, useEffect } from 'react';
import { API_URL, adminHeaders } from './config';

function StatCard({ label, value, subtitle, alert }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #DBD6C8', borderRadius: 12,
      padding: '20px 22px', flex: '1 1 180px', minWidth: 160,
    }}>
      <div style={{
        fontSize: 32, fontWeight: 700, color: alert ? '#C5392F' : '#182B45',
        fontFamily: "'Fraunces', Georgia, serif",
      }}>{value}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#1A1E26', marginTop: 4 }}>{label}</div>
      {subtitle && <div style={{ fontSize: 12, color: '#565C66', marginTop: 2 }}>{subtitle}</div>}
    </div>
  );
}

function MetricRow({ label, value, alert }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '10px 0', borderBottom: '1px solid #eee',
    }}>
      <span style={{ fontSize: 14, color: '#565C66' }}>{label}</span>
      <span style={{
        fontSize: 14, fontWeight: 600,
        color: alert ? '#C5392F' : '#1A1E26',
      }}>{value}</span>
    </div>
  );
}

function SectionHeader({ children }) {
  return (
    <h3 style={{
      fontFamily: "'Fraunces', Georgia, serif", fontSize: 18, fontWeight: 600,
      color: '#182B45', margin: '28px 0 12px', paddingBottom: 8,
      borderBottom: '2px solid #182B45',
    }}>{children}</h3>
  );
}

export default function AdminMetrics() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
        fetch(`${API_URL}/api/admin/metrics`, {
      headers: adminHeaders(),
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => { setMetrics(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#565C66' }}>Loading metrics...</div>;
  if (error) return <div style={{ padding: 40, textAlign: 'center', color: '#C5392F' }}>Error: {error}</div>;
  if (!metrics) return null;

  const m = metrics;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Top-line cards */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 8 }}>
        <StatCard label="Total Users" value={m.signups.signups_all_time} subtitle={`${m.signups.signups_7d} this week`} />
        <StatCard label="Active Subscriptions" value={m.subscriptions.active_subs}
          subtitle={`${m.subscriptions.active_individual} ind · ${m.subscriptions.active_family} fam`} />
        <StatCard label="Student Profiles" value={m.students.total_students}
          subtitle={`${m.students.students_with_matches} with matches`} />
        <StatCard label="Est. ARR" value={`$${(m.revenue.arr_estimate || 0).toLocaleString()}`} />
      </div>

      {/* Alerts row */}
      {(parseInt(m.subscriptions.past_due) > 0 || parseInt(m.engine.stuck) > 0 || parseInt(m.emails.pending_or_failed) > 0) && (
        <div style={{
          background: '#FFF5F5', border: '1px solid #FED7D7', borderRadius: 10,
          padding: '14px 18px', marginBottom: 20, fontSize: 14, color: '#C5392F',
        }}>
          ⚠️ {parseInt(m.subscriptions.past_due) > 0 && `${m.subscriptions.past_due} past-due subscription(s). `}
          {parseInt(m.engine.stuck) > 0 && `${m.engine.stuck} stuck matching run(s). `}
          {parseInt(m.emails.pending_or_failed) > 0 && `${m.emails.pending_or_failed} unsent email(s). `}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Left column */}
        <div>
          <SectionHeader>Signups</SectionHeader>
          <MetricRow label="Today" value={m.signups.signups_today} />
          <MetricRow label="Last 7 days" value={m.signups.signups_7d} />
          <MetricRow label="Last 30 days" value={m.signups.signups_30d} />
          <MetricRow label="All time" value={m.signups.signups_all_time} />

          <SectionHeader>Subscriptions</SectionHeader>
          <MetricRow label="Active (Individual)" value={m.subscriptions.active_individual} />
          <MetricRow label="Active (Family)" value={m.subscriptions.active_family} />
          <MetricRow label="Canceling" value={m.subscriptions.canceling} />
          <MetricRow label="Past Due" value={m.subscriptions.past_due} alert={parseInt(m.subscriptions.past_due) > 0} />
        </div>

        {/* Right column */}
        <div>
          <SectionHeader>Matching Engine</SectionHeader>
          <MetricRow label="Completed runs" value={m.engine.completed_runs} />
          <MetricRow label="In progress" value={m.engine.in_progress} />
          <MetricRow label="Stuck (>15 min)" value={m.engine.stuck} alert={parseInt(m.engine.stuck) > 0} />
          <MetricRow label="Errors" value={m.engine.errored} alert={parseInt(m.engine.errored) > 0} />
          <MetricRow label="Runs (7d)" value={m.engine.runs_7d} />
          <MetricRow label="Avg duration" value={`${m.engine.avg_duration_sec || 0}s`} />
          <MetricRow label="API cost (30d)" value={`$${m.engine.cost_30d || '0.00'}`} />

          <SectionHeader>Onboarding Emails</SectionHeader>
          <MetricRow label="Sent" value={m.emails.total_sent} />
          <MetricRow label="Queued (future)" value={m.emails.queued_future} />
          <MetricRow label="Pending / Failed" value={m.emails.pending_or_failed} alert={parseInt(m.emails.pending_or_failed) > 0} />
        </div>
      </div>

      {/* Recent signups table */}
      <SectionHeader>Recent Signups</SectionHeader>
      <div style={{
        background: '#fff', border: '1px solid #DBD6C8', borderRadius: 12,
        overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#F5F2EA', borderBottom: '1px solid #DBD6C8' }}>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#565C66', letterSpacing: '0.04em' }}>NAME</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#565C66', letterSpacing: '0.04em' }}>EMAIL</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#565C66', letterSpacing: '0.04em' }}>ROLE</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#565C66', letterSpacing: '0.04em' }}>PLAN</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, fontSize: 12, color: '#565C66', letterSpacing: '0.04em' }}>STATUS</th>
              <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: 600, fontSize: 12, color: '#565C66', letterSpacing: '0.04em' }}>SIGNED UP</th>
            </tr>
          </thead>
          <tbody>
            {m.recentSignups.map((u, i) => (
              <tr key={i} style={{ borderBottom: i < m.recentSignups.length - 1 ? '1px solid #eee' : 'none' }}>
                <td style={{ padding: '10px 16px', fontWeight: 600 }}>{u.full_name || '—'}</td>
                <td style={{ padding: '10px 16px', color: '#565C66' }}>{u.email}</td>
                <td style={{ padding: '10px 16px' }}>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 4,
                    background: u.role === 'parent' ? '#EEF6FF' : u.role === 'student' ? '#E8F5EE' : '#F5F2EA',
                    color: u.role === 'parent' ? '#2563EB' : u.role === 'student' ? '#16a765' : '#565C66',
                  }}>{u.role}</span>
                </td>
                <td style={{ padding: '10px 16px', color: '#565C66' }}>{u.plan_type || '—'}</td>
                <td style={{ padding: '10px 16px' }}>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 4,
                    background: u.subscription_status === 'active' ? '#E8F5EE' : u.subscription_status === 'past_due' ? '#FFF5F5' : '#F5F2EA',
                    color: u.subscription_status === 'active' ? '#16a765' : u.subscription_status === 'past_due' ? '#C5392F' : '#565C66',
                  }}>{u.subscription_status || '—'}</span>
                </td>
                <td style={{ padding: '10px 16px', color: '#565C66', textAlign: 'right' }}>
                  {new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Denver' })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 12, color: '#999', marginTop: 16, textAlign: 'right' }}>
        Last updated: {new Date(m.generatedAt).toLocaleString('en-US', { timeZone: 'America/Denver' })}
      </div>
    </div>
  );
}
